import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import '@/App.css';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';

// Lazy load Chatbot
const Chatbot = lazy(() => import('@/components/Chatbot'));
import ScrollToTop from '@/components/ScrollToTop';

// Delay chatbot loading
const LazyLoadedChatbot = () => {
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    // Load after 3 seconds or on user interaction
    const timer = setTimeout(() => setShouldLoad(true), 3000);
    
    const handleInteraction = () => {
      setShouldLoad(true);
      cleanup();
    };
    
    const cleanup = () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleInteraction);
      window.removeEventListener('click', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
    };

    window.addEventListener('scroll', handleInteraction, { once: true, passive: true });
    window.addEventListener('click', handleInteraction, { once: true });
    window.addEventListener('touchstart', handleInteraction, { once: true, passive: true });

    return cleanup;
  }, []);

  return shouldLoad ? <Chatbot /> : null;
};

// Public Pages
import HomePage from '@/pages/HomePage';
import AboutPage from '@/pages/AboutPage';
import ServicesPage from '@/pages/ServicesPage';
import ProjectsPage from '@/pages/ProjectsPage';
import BlogPage from '@/pages/BlogPage';
import BlogDetailPage from '@/pages/BlogDetailPage';
import TeamPage from '@/pages/TeamPage';
import ContactPage from '@/pages/ContactPage';

// Admin Pages
import AdminLogin from '@/pages/admin/AdminLogin';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminServices from '@/pages/admin/AdminServices';
import AdminProjects from '@/pages/admin/AdminProjects';
import AdminBlog from '@/pages/admin/AdminBlog';
import AdminTeam from '@/pages/admin/AdminTeam';
import AdminMessages from '@/pages/admin/AdminMessages';
import AdminPages from '@/pages/admin/AdminPages';
import AdminSettings from '@/pages/admin/AdminSettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios interceptor for auth
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  return token ? children : <Navigate to="/admin/login" />;
};

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/hakkimizda" element={<AboutPage />} />
          <Route path="/hizmetler" element={<ServicesPage />} />
          <Route path="/projeler" element={<ProjectsPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogDetailPage />} />
          <Route path="/ekip" element={<TeamPage />} />
          <Route path="/iletisim" element={<ContactPage />} />
          
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/hizmetler" element={<ProtectedRoute><AdminServices /></ProtectedRoute>} />
          <Route path="/admin/projeler" element={<ProtectedRoute><AdminProjects /></ProtectedRoute>} />
          <Route path="/admin/blog" element={<ProtectedRoute><AdminBlog /></ProtectedRoute>} />
          <Route path="/admin/ekip" element={<ProtectedRoute><AdminTeam /></ProtectedRoute>} />
          <Route path="/admin/mesajlar" element={<ProtectedRoute><AdminMessages /></ProtectedRoute>} />
          <Route path="/admin/sayfalar" element={<ProtectedRoute><AdminPages /></ProtectedRoute>} />
          <Route path="/admin/ayarlar" element={<ProtectedRoute><AdminSettings /></ProtectedRoute>} />
        </Routes>
        {/* Load chatbot only after 5 seconds or on user interaction */}
        <Suspense fallback={null}>
          <LazyLoadedChatbot />
        </Suspense>
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    </HelmetProvider>
  );
}

export default App;
export { API, toast };